import { computed, toValue, type ComputedRef, type MaybeRefOrGetter, type Ref } from 'vue'

import type { ReportViewType } from '../models/chart.types'
import type { ReportColumn } from '../models/column.types'
import type { ReportDataState, ReportResponse, ReportRow, ReportSpec } from '../models/report.types'
import type { ReportTableSortV1 } from '../models/tableConfig.types'
import type { ReportTableType } from '../models/tablePresentation.types'
import { buildComparisonRows, getRenderedComparisonFields } from '../service/comparisonColumns'
import { mapColumns } from '../service/mapColumns'
import { normalizeReportTableRows } from '../service/reportMediaPresentation'
import { buildReportTablePresenterSnapshot } from '../service/reportTablePresenterSnapshot'
import { sortRows } from '../service/sortRows'
import { buildTrendTablePresentation } from '../service/trendTablePresentation'

import { useDataVisibleExportActions, type SheetSharePicker } from './useDataVisibleExportActions'
import type { ResolvedDataVisibleTrend } from './useDataVisibleReportRequest'
import { useReportExport } from './useReportExport'

export interface UseDataVisibleExportOptions {
  view: MaybeRefOrGetter<ReportViewType>
  spec: MaybeRefOrGetter<ReportSpec | null>
  settledSpec: MaybeRefOrGetter<ReportSpec | null>
  state: MaybeRefOrGetter<ReportDataState>
  response: MaybeRefOrGetter<ReportResponse | null>
  rows: MaybeRefOrGetter<readonly ReportRow[]>
  tableColumns: MaybeRefOrGetter<readonly ReportColumn[]>
  reportId: MaybeRefOrGetter<number>
  tableType: MaybeRefOrGetter<ReportTableType>
  breakdownField: MaybeRefOrGetter<string | null>
  matrixDimension: MaybeRefOrGetter<string | null>
  pivotMetric: MaybeRefOrGetter<string | null>
  trend: MaybeRefOrGetter<ResolvedDataVisibleTrend | null>
  sort: MaybeRefOrGetter<ReportTableSortV1 | null>
  getColumn: (field: string) => ReportColumn | null | undefined
  name: MaybeRefOrGetter<string>
  /** Same read scope the view fetched with — an export must not widen or narrow it. */
  workspaceIds?: MaybeRefOrGetter<readonly (number | string)[]>
}

export interface UseDataVisibleExport {
  busy: Ref<boolean>
  canExport: ComputedRef<boolean>
  exportSheet: () => Promise<void>
  downloadExcel: () => Promise<void>
  /** The optional "share with" step before a Sheets export (ORP-25238). */
  sharePicker: SheetSharePicker
}

function finiteTotal(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : undefined
}

/** Export only the exact settled footprint; paged data requires controller total metadata. */
export function useDataVisibleExport(options: UseDataVisibleExportOptions): UseDataVisibleExport {
  const totalRowCount = computed(() => {
    const response = toValue(options.response)
    return finiteTotal(response?.pagination?.total ?? response?.total_rows)
  })
  const responseSettled = computed(() => {
    const current = toValue(options.spec)
    const state = toValue(options.state)
    return (
      current !== null &&
      (state === 'ready' || state === 'empty') &&
      JSON.stringify(current) === JSON.stringify(toValue(options.settledSpec)) &&
      (!current.pagination_enabled || totalRowCount.value !== undefined)
    )
  })
  const tablePresentation = computed(() => {
    const rows = [...toValue(options.rows)]
    const columns = [...toValue(options.tableColumns)]
    const trend = toValue(options.trend)
    if (trend) {
      return buildTrendTablePresentation({
        data: rows,
        columns,
        breakdownColumn: trend.breakdownColumn,
        previewColumn: trend.previewColumn,
        dateDimensions: trend.dateDimensions,
        frequency: trend.frequency,
      })
    }
    return buildReportTablePresenterSnapshot({
      rows,
      columns,
      tableType: toValue(options.tableType),
      breakdownField: toValue(options.breakdownField),
      matrixDimension: toValue(options.matrixDimension),
      pivotMetric: toValue(options.pivotMetric),
    })
  })
  const rows = computed(() =>
    toValue(options.view) === 'table' ? tablePresentation.value.rows : [...toValue(options.rows)],
  )
  const columns = computed(() => {
    if (toValue(options.view) === 'table') return tablePresentation.value.columns
    const current = toValue(options.spec)
    if (!current) return []
    const fields = [...(current.dimensions ?? []), ...(current.metrics ?? [])]
    const byField = new Map(
      mapColumns(fields, { getColumn: options.getColumn }).map((column) => [column.field, column]),
    )
    return fields.flatMap((field, index) => {
      const column = byField.get(field)
      return column && fields.indexOf(field) === index ? [column] : []
    })
  })
  const remotePresentationCompatible = computed(
    () =>
      toValue(options.view) !== 'table' ||
      (toValue(options.trend) === null &&
        toValue(options.tableType) === 'table' &&
        !toValue(options.breakdownField)),
  )
  const presentFetchedPresentation = computed<
    ((source: readonly ReportRow[]) => { rows: ReportRow[]; columns: ReportColumn[] }) | undefined
  >(() => {
    if (toValue(options.view) !== 'table') return
    const reportId = toValue(options.reportId)
    const columns = [...toValue(options.tableColumns)]
    const comparisonFields = getRenderedComparisonFields(columns)
    const sort = toValue(options.trend) ? null : toValue(options.sort)
    const trend = toValue(options.trend)
    const tableType = toValue(options.tableType)
    const breakdownField = toValue(options.breakdownField)
    const matrixDimension = toValue(options.matrixDimension)
    const pivotMetric = toValue(options.pivotMetric)

    return (source) => {
      const normalized = normalizeReportTableRows(source, reportId)
      const sorted = sort
        ? sortRows(normalized, sort.field, sort.order, { getColumn: options.getColumn })
        : normalized
      const compared = buildComparisonRows(sorted, comparisonFields)
      if (trend) {
        const presentation = buildTrendTablePresentation({
          data: compared,
          columns,
          breakdownColumn: trend.breakdownColumn,
          previewColumn: trend.previewColumn,
          dateDimensions: trend.dateDimensions,
          frequency: trend.frequency,
        })
        return { rows: presentation.rows, columns: presentation.columns }
      }
      return buildReportTablePresenterSnapshot({
        rows: compared,
        columns,
        tableType,
        breakdownField,
        matrixDimension,
        pivotMetric,
      })
    }
  })
  const transport = useReportExport({
    spec: options.spec,
    rows,
    columns,
    enabled: responseSettled,
    totalRowCount,
    name: options.name,
    remotePresentationCompatible,
    presentFetchedPresentation,
    ...(options.workspaceIds ? { workspaceIds: options.workspaceIds } : {}),
  })
  const actions = useDataVisibleExportActions(transport)
  return {
    busy: transport.busy,
    canExport: transport.canExport,
    exportSheet: actions.exportSheet,
    downloadExcel: actions.downloadExcel,
    sharePicker: actions.sharePicker,
  }
}
